
import { onClickOutside } from '@vueuse/core';
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  Ref,
  watch,
} from 'vue';
import { IError, IMedia } from '@/types/interfaces';
import DriveInput from '../modules/drive/components/UI/DriveInput.vue';
import AppLoader from './UI/AppLoader.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';

export default defineComponent({
  components: {
    DriveInput,
    AppLoader,
  },
  props: {
    file: { type: Object as () => IMedia, required: true },
    mountErrorInfo: { type: Object as PropType<{title: string, error: string}>, required: false },
  },
  emits: ['close', 'open', 'clearMountErrorInfo'],
  setup(props, { emit }) {
    const modal = ref();
    const driveStore = useDriveStore();

    const isLoader: Ref<boolean> = ref(false);

    const mediaItem = computed(() => driveStore.mixedData.data.find((item) => item.file?.id === props.file?.id));
    const mediaTitle = ref(mediaItem.value?.file?.name || props.file?.name);
    const error = ref('');
    const percent: Ref<number> = ref(0);

    const mediaTitleWithoutExtValue = ref('');

    const mediaTitleWithoutExt = computed({
      get: () => {
        const dotIndex = mediaTitle.value.lastIndexOf('.');
        if (dotIndex === -1) {
          return mediaTitle.value;
        }
        return mediaTitle.value.substring(0, dotIndex);
      },
      set: (value) => {
        mediaTitleWithoutExtValue.value = value;
      },
    });

    // initialize mediaTitleWithoutExtValue with the current value of mediaTitleWithoutExt
    mediaTitleWithoutExtValue.value = mediaTitleWithoutExt.value;

    const skipValidationClear = ref(false);

    const disabled = computed(() => !!error.value || !mediaTitle.value);

    function close() {
      emit('close');
    }

    function setError(prevTitle: string, errorTitle: string) {
      mediaTitle.value = prevTitle;
      error.value = errorTitle;
    }
    function onUpdateRequest(per: number): void {
      percent.value = per;
    }
    function renameMedia(): void {
      if (disabled.value) {
        return;
      }
      // isLoader.value = true;
      const nameWithoutExt = mediaTitleWithoutExtValue.value;
      const title = mediaTitle.value;
      const fileExtIndex = props.file.name.lastIndexOf('.');
      const ext = fileExtIndex !== -1 ? props.file.name.substr(fileExtIndex + 1) : '';
      const fileName = `${nameWithoutExt}${ext ? `.${ext}` : ''}`;

      driveStore.renameFile(fileName, props.file.id)
        .then((res) => {
          if (res.success) {
            emit('close');
            // NotificationService.notifySuccess();
          } else {
            const errorData = res?.response?.data;
            if (errorData) {
              if (errorData.errorKey === 'SYSTEM_ERROR') {
                emit('open', { title, error: 'Name is already taken' });
              } else {
                emit('open', { title, error: 'Something gone wrong' });
              }
            }
          }
        });
      isLoader.value = false;
      close();
    }

    function inMediaNameUniq(): boolean {
      const data = Object.values(driveStore.mixedData.data) as any[];
      return !!data.find((media) => media?.file?.name.substring(0, mediaTitle.value.lastIndexOf('.')) === mediaTitleWithoutExtValue.value);
    }

    watch(() => mediaTitleWithoutExtValue.value, () => {
      if (!mediaTitleWithoutExtValue.value) {
        error.value = 'This filed is required';
      } else if (mediaTitleWithoutExtValue.value.length < 4) {
        error.value = 'Folder name must be at least 4 characters long.';
      } else if (mediaTitleWithoutExtValue.value.length > 255) {
        error.value = 'Folder name must be max 255 characters long.';
      } else if (inMediaNameUniq()) {
        error.value = 'Name is already taken';
      } else if (!skipValidationClear.value) {
        error.value = '';
      } else {
        skipValidationClear.value = false;
      }
      mediaTitle.value = mediaTitle.value.replace(/(\r\n|\n|\r)/gm, '');
    });

    // onClickOutside(modal, close);

    onMounted(() => {
      if (props.mountErrorInfo) {
        skipValidationClear.value = true;
        setError(props.mountErrorInfo.title, props.mountErrorInfo.error);
        emit('clearMountErrorInfo');
      }
    });

    return {
      modal,
      error,
      disabled,
      isLoader,
      close,
      renameMedia,
      mediaTitle,
      mediaTitleWithoutExt,
      mediaTitleWithoutExtValue,
    };
  },
});
